import { Step } from "react-joyride";
import { TutorialId } from "../store/slices/tutorialSlice";

// ---------------------------------------------------------------------------
// Step definitions per module
// ---------------------------------------------------------------------------

export type DeviceOnboardingDeployment = "own_robot" | "sandbox";

export interface DeviceOnboardingStepsContext {
  deploymentChoice: DeviceOnboardingDeployment | null;
  registeredDeviceId: string | null;
}

/** Public read-only demo workspace, pre-populated with devices and dashboards. */
export const SANDBOX_URL = "https://app.insaion.com/sandbox";

/**
 * The own-robot branch walks the Add Device wizard one numbered section at a
 * time, in the same order the page presents them. Kept here so the manager can
 * tell "this step lives on /devices/new" without hard-coding indices.
 */
export const DEVICE_ONBOARDING_WIZARD_STEP_IDS = [
  "own-platform",
  "own-enrollment-key",
  "own-agent-config",
  "own-install-command",
  "own-agent-status",
] as const;

export const isDeviceOnboardingWizardStep = (stepId?: string): boolean =>
  !!stepId &&
  (DEVICE_ONBOARDING_WIZARD_STEP_IDS as readonly string[]).includes(stepId);

const dispatchTutorialEvent = (
  name: string,
  detail?: Record<string, unknown>,
) => window.dispatchEvent(new CustomEvent(name, { detail }));

const TutorialChoiceButton = ({
  title,
  description,
  onClick,
}: {
  title: string;
  description: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-lg border border-neutral-700 bg-neutral-800/80 p-3 text-left transition-all hover:border-orange-500 hover:bg-neutral-800"
  >
    <div className="font-semibold text-sm text-white">{title}</div>
    <p className="mt-1 text-xs text-neutral-400">{description}</p>
  </button>
);

const TutorialNextButton = ({ nextTitle }: { nextTitle: string }) => (
  <button
    type="button"
    onClick={() => dispatchTutorialEvent("insaion:tutorialStartNext")}
    className="mt-3 w-full rounded-lg bg-gradient-to-r from-rose-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
  >
    Continue to {nextTitle} →
  </button>
);

export const getDeviceOnboardingSteps = (
  context: DeviceOnboardingStepsContext,
): Step[] => {
  const steps: Step[] = [
    {
      id: "welcome-branch",
      target: "body",
      placement: "center",
      title: "Welcome to INSAION",
      buttons: ["close"],
      content: (
        <div className="space-y-3 text-sm">
          <p>
            This tutorial helps you register your first device and open its
            monitoring dashboard.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TutorialChoiceButton
              title="Own Robot"
              description="Walk through the Add Device wizard: pick a platform, a key, tune the agent, then install it on your robot."
              onClick={() =>
                dispatchTutorialEvent(
                  "insaion:tutorialDeviceOnboardingDeploymentSelected",
                  { choice: "own_robot" },
                )
              }
            />
            <TutorialChoiceButton
              title="Explore the Sandbox"
              description="Open a read-only demo workspace already populated with devices and dashboards. Nothing to install."
              onClick={() =>
                dispatchTutorialEvent(
                  "insaion:tutorialDeviceOnboardingDeploymentSelected",
                  { choice: "sandbox" },
                )
              }
            />
          </div>
        </div>
      ),
    },
  ];

  if (context.deploymentChoice === "own_robot") {
    const agentDetected = !!context.registeredDeviceId;

    steps.push(
      {
        id: "own-platform",
        // Target the container that holds BOTH platform cards so the spotlight
        // shows the full selection area, not just one option.
        target: "#tutorial-platform-selection",
        placement: "auto",
        // Wizard-driven step: picking a platform advances it, not a Next button.
        buttons: ["back", "close"],
        blockTargetInteraction: false,
        title: "Step 1 — Installation method",
        content: (
          <div className="space-y-2 text-sm">
            <p>
              Choose where the agent runs: <strong>Ubuntu / Debian</strong>{" "}
              installs it on the host as a systemd service,{" "}
              <strong>Docker</strong> runs it as a container next to your stack.
            </p>
            <p className="text-neutral-400">
              Both are fully supported. The tutorial continues as soon as you
              pick one.
            </p>
          </div>
        ),
      },
      {
        id: "own-enrollment-key",
        target: "#tutorial-registration-section",
        placement: "auto",
        buttons: ["primary", "back", "close"],
        blockTargetInteraction: false,
        // The key dropdown and the create-key form both open outside the
        // spotlight, so the overlay would only get in the way here.
        hideOverlay: true,
        title: "Step 2 — Enrollment key",
        content: (
          <div className="space-y-2 text-sm">
            <p>
              The agent uses this key to register itself with your organization.
              Pick one of your existing keys, or click <strong>New key</strong>{" "}
              to mint one right here.
            </p>
            <p className="text-neutral-400">
              The install command further down fills itself in with whatever you
              select. Click <strong>Next</strong> once a key is set.
            </p>
          </div>
        ),
      },
      {
        id: "own-agent-config",
        target: "#tutorial-ros-config-section",
        placement: "auto",
        buttons: ["primary", "back", "close"],
        blockTargetInteraction: false,
        // Inputs, radio groups and the advanced accordion all live inside this
        // section — hiding the overlay keeps every one of them usable.
        hideOverlay: true,
        title: "Step 3 — Agent configuration",
        content: (
          <div className="space-y-2 text-sm">
            <p>
              Everything here is optional and the defaults suit most robots.
              Take a moment to set what applies to yours.
            </p>
            <p>
              Two settings matter most: <strong>Middleware (RMW)</strong> and{" "}
              <strong>ROS domain ID</strong>. They must match what your robot
              actually runs, or the agent will not see its topics.
            </p>
            <p className="text-neutral-400">
              Turn off <strong>Robot runs ROS 2</strong> for a non-ROS robot.
              The command in step 4 updates live as you change things.
            </p>
          </div>
        ),
      },
      {
        id: "own-install-command",
        target: "#tutorial-install-command",
        placement: "top",
        buttons: ["primary", "back", "close"],
        blockTargetInteraction: false,
        title: "Step 4 — Run the install command",
        content: (
          <div className="space-y-2 text-sm">
            <p>
              This command carries every choice you just made. Copy it and run
              it on your robot.
            </p>
            <p className="text-neutral-400">
              The enrollment key stays masked — use the eye icon to reveal it.
              Click <strong>Next</strong> to watch the agent come online.
            </p>
          </div>
        ),
      },
      {
        id: "own-agent-status",
        target: "#tutorial-agent-status",
        placement: "top",
        // Autodetection drives this step: the status check finds the agent and
        // the tutorial moves on by itself, so there is no Next until it does.
        buttons: agentDetected
          ? ["primary", "back", "close"]
          : ["back", "close"],
        blockTargetInteraction: false,
        title: agentDetected
          ? "Your agent is connected"
          : "Step 5 — Agent status",
        content: agentDetected ? (
          <div className="space-y-2 text-sm">
            <p className="font-semibold text-emerald-400">
              Agent detected and registered.
            </p>
            <p>
              The checklist confirms it is online, reporting its version, and —
              for a ROS 2 robot — how many topics it can see.
            </p>
            <p className="text-neutral-400">
              Click <strong>Next</strong> to open your device in the fleet list.
            </p>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p>
              INSAION is watching for your agent. Run the command above and this
              panel checks it end to end: registered, connected, version
              reported, ROS graph visible.
            </p>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-500 border-t-orange-500" />
              <span className="text-xs text-neutral-400">
                Waiting for the agent to connect…
              </span>
            </div>
            <p className="text-xs text-neutral-500">
              The tutorial continues on its own the moment your device is
              detected.
            </p>
          </div>
        ),
      },
    );
  }

  if (context.deploymentChoice === "sandbox") {
    steps.push({
      id: "sandbox-redirect",
      target: "body",
      placement: "center",
      // Terminal step of this branch: the sandbox is a separate demo session,
      // so there is no device to wait for here.
      buttons: ["back", "primary"],
      title: "Sandbox Opened",
      content: (
        <div className="space-y-3 text-sm">
          <p>
            The sandbox opened in a new tab. It is a read-only INSAION workspace
            already populated with a couple of devices and their dashboards, so
            you can see what the app looks like once your fleet is connected.
          </p>
          <p className="text-neutral-400">
            Come back to this tab whenever you are ready to register your own
            robot — pick <strong>Own Robot</strong> with Back.
          </p>
          <a
            href={SANDBOX_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-lg border border-neutral-700 bg-neutral-800/80 p-3 text-left text-sm font-semibold text-white transition-all hover:border-orange-500 hover:bg-neutral-800"
          >
            Didn't open? Open the sandbox →
          </a>
        </div>
      ),
    });
  }

  if (context.registeredDeviceId) {
    steps.push({
      id: "registered-monitor",
      target: `[data-tutorial-monitor="${context.registeredDeviceId}"]`,
      placement: "left",
      title: "Device Registered",
      buttons: ["back", "close"],
      content:
        "Your device is registered. Click Monitor to open this device dashboard.",
    });
  }

  if (context.registeredDeviceId) {
    steps.push({
      id: "tutorial-complete",
      target: "body",
      placement: "center",
      title: "Tutorial Complete",
      buttons: ["primary"],
      content: (
        <div>
          <p className="text-emerald-400 font-semibold">Great job.</p>
          <p>
            You completed the first device onboarding. Ready to configure the
            device in <strong>Module 2</strong>.
          </p>
          <TutorialNextButton nextTitle="Device Configuration" />
        </div>
      ),
    });
  }

  return steps;
};

const deviceConfigurationSteps: Step[] = [
  {
    id: "config-intro",
    target: "body",
    placement: "center",
    title: "Device Configuration & Telemetry",
    buttons: ["primary", "close"],
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Open <strong>Devices</strong>, click a registered device, and use the
          left sidebar to navigate between <strong>Overview</strong>,{" "}
          <strong>Data Sources</strong>, <strong>Telemetry</strong>, and{" "}
          <strong>Rolling Buffer</strong>.
        </p>
        <p className="text-neutral-400">
          Click <strong>Next</strong> to go to the Devices page.
        </p>
      </div>
    ),
  },
  {
    id: "config-open-device",
    target: "#tutorial-devices-table",
    placement: "auto",
    scrollTarget: "#registered-devices-header",
    title: "Open a Registered Device",
    buttons: ["back", "close"],
    content:
      "Click any registered device in this table to open its details page. A device you have not configured yet opens straight on Data collection.",
  },
  {
    id: "config-navigate-to-sources",
    target: "#tutorial-config-nav-sources",
    placement: "bottom",
    buttons: ["back", "close"],
    blockTargetInteraction: false,
    title: "Open Data Collection",
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Open the <strong>Data collection</strong> tab to see every topic this
          device publishes, and what it does with each one.
        </p>
        <p className="text-neutral-400">
          The tutorial will continue automatically once you click it.
        </p>
      </div>
    ),
  },
  {
    id: "config-monitored-sources",
    target: "#monitored-topics-section",
    placement: "auto",
    hideOverlay: true,
    buttons: ["primary", "back", "close"],
    title: "Choose What to Monitor",
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Click <strong>Edit</strong>, then tick topics in the{" "}
          <strong>Monitor</strong> column to ingest them as live telemetry for
          dashboards and alerts.
        </p>
        <p>
          Typical examples include{" "}
          <code className="bg-neutral-800 px-1 rounded">/cmd_vel</code> or{" "}
          <code className="bg-neutral-800 px-1 rounded">/battery_state</code>.
          All nested fields of a ticked topic are captured automatically.
        </p>
        <p className="text-neutral-400 text-xs">
          Saving pushes the config to the device agent automatically.
        </p>
      </div>
    ),
  },
  {
    id: "config-recording-settings",
    target: "#tutorial-rolling-buffer-section",
    placement: "auto",
    buttons: ["primary", "back", "close"],
    title: "Set Up the Rolling Buffer",
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Expand <strong>Rolling buffer settings</strong> to configure the
          on-device recording engine: switch recording on, then set the
          retention policy and storage quota.
        </p>
        <p className="text-neutral-400 text-xs">
          <strong>Policy</strong> controls how data is retained once the quota
          fills (for example FIFO, which drops the oldest recordings), and{" "}
          <strong>Size</strong> caps how much space recordings may use on the
          device.
        </p>
      </div>
    ),
  },
  {
    id: "config-rolling-buffer-basics",
    target: "#topics-tree",
    placement: "auto",
    title: "Choose What to Record",
    buttons: ["primary", "back", "close"],
    content: (
      <div className="space-y-2 text-sm">
        <p>
          The <strong>Record</strong> column writes a topic to MCAP files in the
          rolling buffer. A topic can be monitored, recorded, or both, so tick
          whichever columns you need on the same row.
        </p>
        <p className="text-neutral-400 text-xs">
          Heavy topics such as{" "}
          <code className="bg-neutral-800 px-1 rounded">
            sensor_msgs/msg/Image
          </code>{" "}
          cannot be monitored as telemetry, but they are exactly what you want
          to record for playback.
        </p>
      </div>
    ),
  },
  {
    id: "config-rolling-buffer-pipelines",
    target: "#topics-tree",
    placement: "auto",
    title: "Record Groups at Once",
    buttons: ["primary", "back", "close"],
    content: (
      <div className="space-y-2 text-sm">
        <p>
          A namespace header ticks every topic beneath it, and{" "}
          <strong>Rules</strong> capture whole groups with a pattern such as{" "}
          <code className="bg-neutral-800 px-1 rounded">/camera/*</code> —
          matched rows show an asterisk in the Record column.
        </p>
        <p>
          Ticking a matched topic off adds an exclusion rather than deleting the
          rule, so{" "}
          <code className="bg-neutral-800 px-1 rounded">/velodyne_points</code>{" "}
          can be dropped while the rest of the pattern keeps recording.
        </p>
        <p className="text-neutral-400 text-xs">
          These recordings are the foundation for incident playback in{" "}
          <strong>Module 4</strong>.
        </p>
      </div>
    ),
  },
  {
    id: "config-complete",
    target: "body",
    placement: "center",
    title: "Tutorial Complete",
    buttons: ["primary"],
    content: (
      <div className="space-y-2 text-sm">
        <p className="text-emerald-400 font-semibold">
          Great job. You completed Module 2.
        </p>
        <p>
          Your device telemetry and recording setup is ready. Continue to{" "}
          <strong>Module 3</strong> to build dashboards and visualize data.
        </p>
        <TutorialNextButton nextTitle="Visualizing Telemetry" />
      </div>
    ),
  },
];

const dashboardPanelsSteps: Step[] = [
  // Step 0 — intro, navigate to /monitoring when user clicks Next
  {
    id: "dash-intro",
    target: "body",
    placement: "center",
    title: "Build a Fleet Overview Panel",
    buttons: ["primary", "close"],
    content: (
      <div className="space-y-2 text-sm">
        <p>
          In this module we&apos;ll create a <strong>Host Map</strong> panel on
          the Default dashboard to visualise CPU usage across your entire fleet
          at a glance.
        </p>
        <p className="text-neutral-400">
          Click <strong>Next</strong> to open the Monitoring page.
        </p>
      </div>
    ),
  },
  // Step 1 — select default dashboard from the title switcher
  {
    id: "dash-select-default",
    target: "#tutorial-dashboard-switcher",
    placement: "bottom",
    title: "Open the Default Dashboard",
    buttons: ["primary", "back", "close"],
    blockTargetInteraction: false,
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Click the dashboard name at the top of the page to open the dashboard
          list, then pick <strong>Default</strong>.
        </p>
        <p className="text-neutral-400">
          Click <strong>Next</strong> when the dashboard is open.
        </p>
      </div>
    ),
  },
  // Step 2 — three-dot menu → Add Panel (wizard-driven, no primary button)
  // Target body/center so the tooltip is in the middle of the screen and does
  // NOT overlap the dropdown that opens in the top-right corner.
  {
    id: "dash-add-panel",
    target: "body",
    placement: "center",
    title: "Add a Panel",
    buttons: ["back", "close"],
    blockTargetInteraction: false,
    hideOverlay: true,
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Click the <strong>⋮</strong> menu in the top-right of the dashboard
          header, then select <strong>Add Panel</strong>. The panel editor will
          open automatically and this tutorial will continue.
        </p>
      </div>
    ),
  },
  // Step 3 — panel title + visualization type
  {
    id: "dash-panel-general",
    target: "#tutorial-panel-general",
    placement: "auto",
    title: "Panel Title & Visualization Type",
    buttons: ["primary", "back", "close"],
    blockTargetInteraction: false,
    hideOverlay: true,
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Give your panel a name — e.g. <strong>CPU Fleet Overview</strong>.
        </p>
        <p>
          Then set <strong>Visualization Type</strong> to{" "}
          <strong>Host Map</strong>. Each hexagon in the map represents one
          device, color-coded by value.
        </p>
        <p className="text-neutral-400">
          Click <strong>Next</strong> when done.
        </p>
      </div>
    ),
  },
  // Step 4 — query builder
  {
    id: "dash-panel-queries",
    target: "#tutorial-panel-queries",
    placement: "top",
    title: "Build the Query",
    buttons: ["primary", "back", "close"],
    blockTargetInteraction: false,
    hideOverlay: true,
    content: (
      <div className="space-y-2 text-sm">
        <p>
          In the <strong>Queries</strong> section, click{" "}
          <strong>Add query</strong> and build the following path:
        </p>
        <code className="block bg-neutral-800 rounded px-2 py-1.5 text-xs text-orange-300 leading-relaxed">
          devices → * → monitored_metrics → /host_metrics/cpu → usage_active
        </code>
        <p className="text-neutral-400">
          The <code className="text-orange-300 text-[11px]">*</code> wildcard
          selects all devices so every robot appears on the map.
        </p>
        <p className="text-neutral-400">
          Click <strong>Next</strong> when the query is ready.
        </p>
      </div>
    ),
  },
  // Step 5 — thresholds / color coding
  // Target body so this step is always reachable regardless of viz type,
  // while hideOverlay keeps the panel editor fully interactive.
  {
    id: "dash-panel-thresholds",
    target: "body",
    placement: "center",
    title: "Color-Code by Threshold",
    buttons: ["primary", "back", "close"],
    blockTargetInteraction: false,
    hideOverlay: true,
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Make sure <strong>Host Map</strong> is selected, then scroll to{" "}
          <strong>Host Map Options → Thresholds</strong>. Add color bands to
          make status obvious at a glance — for example:
        </p>
        <ul className="space-y-1 text-xs text-neutral-300">
          <li>
            <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1 align-middle" />
            <strong>{"< 50"}</strong> — green (healthy)
          </li>
          <li>
            <span className="inline-block w-3 h-3 rounded-full bg-yellow-400 mr-1 align-middle" />
            <strong>{"< 80"}</strong> — yellow (warning)
          </li>
          <li>
            <span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-1 align-middle" />
            <strong>{"< 100"}</strong> — red (critical)
          </li>
        </ul>
        <p className="text-neutral-400">
          Use the <strong>Preset</strong> dropdown for a ready-made color
          scheme, or add thresholds manually.
        </p>
        <p className="text-neutral-400">
          Click <strong>Next</strong> when done.
        </p>
      </div>
    ),
  },
  // Step 6 — save panel
  {
    id: "dash-panel-save",
    target: "#tutorial-panel-save-btn",
    placement: "bottom",
    title: "Save the Panel",
    buttons: ["back", "close"],
    blockTargetInteraction: false,
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Click <strong>Save Panel</strong> — your Host Map will be added to the
          dashboard and the tutorial will finish automatically.
        </p>
      </div>
    ),
  },
  // Step 7 — complete
  {
    id: "dash-complete",
    target: "body",
    placement: "center",
    title: "Fleet Overview Live!",
    buttons: ["primary"],
    content: (
      <div className="space-y-2 text-sm">
        <p className="text-emerald-400 font-semibold">
          Great work — Module 3 complete.
        </p>
        <p>
          Your Host Map panel is now live on the dashboard. You can monitor CPU
          levels across every device in your fleet at a glance, with color
          thresholds highlighting any devices that need attention.
        </p>
        <p>
          Continue to <strong>Module 4</strong> to explore incident playback and
          the multimodal viewer.
        </p>
        <TutorialNextButton nextTitle="Recordings & Multimodal Viewer" />
      </div>
    ),
  },
];

const lichtblickRecordingsSteps: Step[] = [
  {
    id: "lichtblick-intro",
    target: "body",
    placement: "center",
    title: "Sync and Visualize Multimodal Data",
    buttons: ["primary", "close"],
    content: (
      <div className="space-y-2 text-sm">
        <p>
          In this module you'll pull a slice from a device's rolling buffer and
          open it in Lichtblick for synchronized playback.
        </p>
        <p>
          Choose a device that already has its <strong>Rolling Buffer</strong>{" "}
          configured from <strong>Module 2</strong>.
        </p>
        <p className="text-neutral-400">
          Click <strong>Next</strong> to open the Devices page.
        </p>
      </div>
    ),
  },
  {
    id: "lichtblick-open-device",
    target: "#tutorial-devices-table",
    placement: "auto",
    title: "Choose a Device",
    buttons: ["back", "close"],
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Open a device that already has rolling buffer pipelines configured.
        </p>
        <p className="text-neutral-400">
          Click its row to open Device Details. The tutorial will continue on
          that device.
        </p>
      </div>
    ),
  },
  {
    id: "lichtblick-open-recordings-tab",
    target: "#tutorial-recordings-tab",
    placement: "bottom",
    title: "You're on Recordings",
    buttons: ["primary", "back", "close"],
    content: (
      <div className="space-y-2 text-sm">
        <p>
          A configured device opens on <strong>Recordings</strong>. This is
          where you pull multimodal or high-frequency slices from the device and
          review past uploads.
        </p>
        <p className="text-neutral-400">
          Data collection is one tab away whenever you need to change what this
          robot captures.
        </p>
      </div>
    ),
  },
  {
    id: "lichtblick-timeline",
    target: "#tutorial-recordings-timeline",
    placement: "bottom",
    title: "Explore the Timeline",
    buttons: ["back", "close"],
    hideOverlay: true,
    blockTargetInteraction: false,
    content: (
      <div className="space-y-2 text-sm">
        <p>
          This timeline is interactive: scroll to zoom, drag left and right to
          move through time, and use the calendar to jump to a specific date.
        </p>
        <p>
          Look for a <strong>yellow</strong> recordings block. Those segments
          are still available on the device disk.
        </p>
        <p className="text-neutral-400">
          Drag across any part of that yellow block to select a time window.
        </p>
      </div>
    ),
  },
  {
    id: "lichtblick-upload-selection",
    target: "#tutorial-recording-upload-btn",
    placement: "auto",
    title: "Upload the Selected Slice",
    buttons: ["back", "close"],
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Review the selected start and end time, keep the pipelines you want,
          then click <strong>Upload to Cloud</strong>.
        </p>
        <p className="text-neutral-400">
          The upload request will retrieve just this window from the rolling
          buffer.
        </p>
      </div>
    ),
  },
  {
    id: "lichtblick-recordings-list",
    target: "#tutorial-recordings-list",
    placement: "top",
    title: "Open the Uploaded Recording",
    buttons: ["back", "close"],
    content: (
      <div className="space-y-2 text-sm">
        <p>
          Watch the upload progress here. Once the slice finishes uploading,
          find the recording in the list and expand it if needed.
        </p>
        <p>
          When the cloud segment is ready, click the <strong>eye</strong> icon
          to open it in Lichtblick.
        </p>
      </div>
    ),
  },
  {
    id: "lichtblick-complete",
    target: "body",
    placement: "center",
    title: "Viewer Ready",
    buttons: ["primary"],
    content: (
      <div className="space-y-2 text-sm">
        <p className="text-emerald-400 font-semibold">
          Viewer opened successfully.
        </p>
        <p>
          You can now correlate high-frequency telemetry, multimodal sensor
          streams, plots, and 3D data in a single synchronized view.
        </p>
        <p>
          Use this workflow any time you need to slice a recording from the
          rolling buffer and inspect it in Lichtblick.
        </p>
      </div>
    ),
  },
];

export const tutorialSteps: Record<TutorialId, Step[]> = {
  device_onboarding: getDeviceOnboardingSteps({
    deploymentChoice: null,
    registeredDeviceId: null,
  }),
  device_configuration: deviceConfigurationSteps,
  dashboard_panels: dashboardPanelsSteps,
  lichtblick_recordings: lichtblickRecordingsSteps,
};
